import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';

const CompetitorMisspelledWords = ({ competitor, selectedBee, onBack }) => {
  const { user } = useAuth();
  const [misspelledWords, setMisspelledWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMisspelledWords();
  }, [competitor, selectedBee]);

  const loadMisspelledWords = async () => {
    if (!competitor || !selectedBee || !user) return;

    try {
      setLoading(true);
      setError(null);

      const response = await ApiService.getCompetitorMisspelledWords(
        competitor.competitor_id,
        selectedBee.bee_id,
        user.user_id
      );

      if (response.misspelled_words) {
        setMisspelledWords(response.misspelled_words);
      } else {
        setError('Failed to load misspelled words');
      }
    } catch (err) {
      setError('Failed to load misspelled words: ' + err.message);
      console.error('Error loading misspelled words:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);

    // If less than 24 hours, show relative time
    if (diffHours < 24) {
      const hours = Math.floor(diffHours);
      if (hours === 0) {
        const minutes = Math.floor((now - date) / (1000 * 60));
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      }
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }

    // Otherwise show absolute date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getResultBadgeColor = (result) => {
    return result === 'wrong' ? '#dc3545' : '#fd7e14';
  };

  if (loading) {
    return (
      <div className="card">
        <div className="loading">Loading misspelled words...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">
          <h3>Error</h3>
          <p>{error}</p>
          <button
            onClick={loadMisspelledWords}
            className="button"
            style={{ marginTop: '10px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="misspelled-header">
        <button onClick={onBack} className="back-button">
          ← Back
        </button>
        <h2 className="card-title">Misspelled Words (48 Hours)</h2>
        <p className="card-subtitle">
          {competitor.name} • {selectedBee.name}
        </p>
      </div>

      {misspelledWords.length === 0 ? (
        <div className="empty-state">
          <p>No misspelled words in the past 48 hours!</p>
          <p style={{ fontSize: '0.9rem', color: '#666' }}>
            Great work! All attempts in the past 48 hours were correct.
          </p>
        </div>
      ) : (
        <div className="words-table-container">
          <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px', textAlign: 'center' }}>
            Showing {misspelledWords.length} word{misspelledWords.length !== 1 ? 's' : ''} (oldest first)
          </p>
          <table className="words-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Word</th>
                <th style={{ textAlign: 'center' }}>Result</th>
                <th style={{ textAlign: 'right' }}>Last Attempt</th>
              </tr>
            </thead>
            <tbody>
              {misspelledWords.map((item, index) => (
                <tr key={index}>
                  <td style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                    {item.word}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      className="result-badge"
                      style={{
                        backgroundColor: getResultBadgeColor(item.result),
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}
                    >
                      {item.result}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', color: '#666', fontSize: '0.9rem' }}>
                    {formatTimestamp(item.datetime)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style jsx>{`
        .misspelled-header {
          margin-bottom: 30px;
        }

        .back-button {
          background: none;
          border: none;
          color: #007bff;
          cursor: pointer;
          font-size: 1rem;
          padding: 5px 0;
          margin-bottom: 15px;
          transition: color 0.2s;
        }

        .back-button:hover {
          color: #0056b3;
        }

        .card-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 0.5rem;
        }

        .card-subtitle {
          font-size: 1rem;
          color: #718096;
          margin-bottom: 1rem;
        }

        .words-table-container {
          overflow-x: auto;
        }

        .words-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }

        .words-table thead {
          background: #f8f9fa;
          border-bottom: 2px solid #e9ecef;
        }

        .words-table th {
          padding: 12px 15px;
          font-size: 0.85rem;
          font-weight: 600;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .words-table td {
          padding: 15px;
          border-bottom: 1px solid #e9ecef;
        }

        .words-table tbody tr:hover {
          background: #f8f9fa;
        }

        .words-table tbody tr:last-child td {
          border-bottom: none;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .empty-state p:first-child {
          font-size: 1.2rem;
          color: #333;
          margin-bottom: 10px;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .error {
          text-align: center;
          color: #dc3545;
        }

        @media (max-width: 768px) {
          .words-table {
            font-size: 0.9rem;
          }

          .words-table th,
          .words-table td {
            padding: 10px 8px;
          }

          .card-title {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CompetitorMisspelledWords;
